import { useCallback, useEffect, useState } from 'react';
import { applyTheme } from './applyTheme';
import { DEFAULT_THEME_ID, getTheme, THEMES } from './themes';
import type { Theme } from './tokens';

const STORAGE_KEY = 'orders.themeId';

function storedThemeId(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
}

/**
 * Apply the stored scheme before React paints, so the first frame is already
 * the right colours rather than flashing the :root defaults. Called from
 * main.tsx, above the render.
 */
export function bootstrapTheme(): void {
  applyTheme(getTheme(storedThemeId()));
}

export function useTheme(): { theme: Theme; themes: Theme[]; setThemeId: (id: string) => void } {
  const [themeId, setThemeIdState] = useState<string>(storedThemeId);

  useEffect(() => {
    applyTheme(getTheme(themeId));
  }, [themeId]);

  const setThemeId = useCallback((id: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // A browser with site data blocked still gets the theme for this session;
      // only the memory of the choice is lost.
    }
    setThemeIdState(id);
  }, []);

  return { theme: getTheme(themeId), themes: THEMES, setThemeId };
}
